/**
 * Shared record types for the event workflow tools (guests, sessions, vendors, quotes, expenses, rooms).
 *
 * Every tool that touches one of these reads and writes the same shape, so a list entered once can move
 * between tools, be saved in one project file, and be exchanged as CSV. Records are flat on purpose: a
 * spreadsheet row maps to one record with no nesting. Money is an amount plus an ISO 4217 currency code.
 * Nothing here is stored on a server; see `projectFile.ts` and `projectStore.ts`.
 */

export const GUEST_STATUSES = ["invited", "confirmed", "declined", "waitlist", "attended"] as const;
export type GuestStatus = (typeof GUEST_STATUSES)[number];

export interface Guest {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  organisation?: string;
  role?: string;
  status: GuestStatus;
  /** Food needs, free text ("vegetarian", "no nuts"). */
  dietary?: string;
  /** Access needs, free text ("step-free", "sign language"). */
  access?: string;
  /** True when the guest needs a room. */
  needsStay: boolean;
  /** YYYY-MM-DD */
  arrival?: string;
  /** YYYY-MM-DD */
  departure?: string;
  /** 24-hour HH:MM the guest lands (local time at the airport or station). */
  arrivalTime?: string;
  /** Flight, train or other service number of the arrival, as typed (for example "BA 117"). */
  arrivalFlight?: string;
  /** 24-hour HH:MM of the departure service. */
  departureTime?: string;
  departureFlight?: string;
  table?: string;
  /** Seating: guests with the same label here sit at one table ("Smith family"). */
  together?: string;
  /** Seating: names of guests to keep at other tables, separated by semicolons ("Dan Lee; Eve Ng"). Works both ways. */
  apart?: string;
  /** Extra people coming with this guest. */
  plusOnes?: number;
  notes?: string;
}

export interface Session {
  id: string;
  title: string;
  /** YYYY-MM-DD */
  day: string;
  /** 24-hour HH:MM */
  start: string;
  /** 24-hour HH:MM */
  end?: string;
  track?: string;
  room?: string;
  presenter?: string;
  /** Join link for a live or hybrid session. */
  link?: string;
  /** IANA time zone the times are in; the project's zone when blank. */
  timeZone?: string;
  notes?: string;
}

export interface Vendor {
  id: string;
  name: string;
  category?: string;
  contact?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export const QUOTE_STATUSES = ["requested", "received", "accepted", "rejected"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export interface Quote {
  id: string;
  vendor: string;
  item: string;
  amount: number;
  currency: string;
  /** YYYY-MM-DD */
  validUntil?: string;
  status: QuoteStatus;
  notes?: string;
  /** Optional inputs for the weighted score on the comparison page. */
  rating?: number;
  depositPct?: number;
  cancellation?: CancellationTerms;
}

export const CANCELLATION_TERMS = ["flexible", "moderate", "strict"] as const;
export type CancellationTerms = (typeof CANCELLATION_TERMS)[number];

export const EXPENSE_KINDS = ["cost", "income"] as const;
export type ExpenseKind = (typeof EXPENSE_KINDS)[number];

/** How a tax line is described. Rates are percentages and are always entered, never assumed. */
export const TAX_TYPES = ["none", "sales_tax", "vat", "gst"] as const;
export type TaxType = (typeof TAX_TYPES)[number];

/** Income that is promised but not yet received is "pledged"; a blank status means received. Costs have no status. */
export const INCOME_STATUSES = ["paid", "pledged"] as const;
export type IncomeStatus = (typeof INCOME_STATUSES)[number];

export interface Expense {
  id: string;
  /** YYYY-MM-DD */
  date: string;
  description: string;
  kind: ExpenseKind;
  category?: string;
  vendor?: string;
  amount: number;
  currency: string;
  taxType: TaxType;
  /** Percentage, e.g. 8.25. */
  taxRate?: number;
  /** True when `amount` already includes the tax. */
  taxIncluded: boolean;
  /** Income only: "pledged" is promised, not received, and is kept out of the profit figure. Blank means received. */
  status?: IncomeStatus;
  /** Receipt number or file name, for reference only. */
  receipt?: string;
  notes?: string;
}

export interface Room {
  id: string;
  guest: string;
  hotel?: string;
  roomType?: string;
  /** YYYY-MM-DD */
  checkIn: string;
  /** YYYY-MM-DD */
  checkOut: string;
  /** Name of the person sharing the room. */
  sharedWith?: string;
  notes?: string;
}

export interface Task {
  id: string;
  title: string;
  /** YYYY-MM-DD */
  due?: string;
  done: boolean;
  /** Who is responsible. */
  owner?: string;
  category?: string;
  notes?: string;
}

/** A planned amount for one spending category. Actual spend comes from the expense records with the same category. */
export interface BudgetLine {
  id: string;
  category: string;
  planned: number;
  /** ISO 4217 code. */
  currency: string;
  notes?: string;
}

export const EXHIBITOR_STATUSES = ["enquiry", "booked", "paid", "cancelled"] as const;
export type ExhibitorStatus = (typeof EXHIBITOR_STATUSES)[number];

/** A company taking a booth or stand. */
export interface Exhibitor {
  id: string;
  company: string;
  /** Booth or stand number or label. */
  booth?: string;
  contact?: string;
  email?: string;
  phone?: string;
  status: ExhibitorStatus;
  /** Booth fee agreed. */
  fee?: number;
  /** Amount received so far. */
  paid?: number;
  currency: string;
  /** Power, wifi, furniture, anything they asked for. */
  needs?: string;
  notes?: string;
}

export interface ProjectData {
  guests: Guest[];
  sessions: Session[];
  vendors: Vendor[];
  quotes: Quote[];
  expenses: Expense[];
  rooms: Room[];
  tasks: Task[];
  budgets: BudgetLine[];
  exhibitors: Exhibitor[];
}

export type RecordKind = keyof ProjectData;

export const RECORD_KINDS: readonly RecordKind[] = ["guests", "sessions", "vendors", "quotes", "expenses", "rooms", "tasks", "budgets", "exhibitors"];

export interface RecordMap {
  guests: Guest;
  sessions: Session;
  vendors: Vendor;
  quotes: Quote;
  expenses: Expense;
  rooms: Room;
  tasks: Task;
  budgets: BudgetLine;
  exhibitors: Exhibitor;
}

export const PAPER_SIZES = ["letter", "a4"] as const;
export type PaperSize = (typeof PAPER_SIZES)[number];

export const emptyData = (): ProjectData => ({ guests: [], sessions: [], vendors: [], quotes: [], expenses: [], rooms: [], tasks: [], budgets: [], exhibitors: [] });
